import { createAnimation, type Animation } from "@ionic/react";
import { UI_MOTION } from "../../config/uiMotion.configuration";

const REDUCED_MOTION_MEDIA_QUERY = "(prefers-reduced-motion: reduce)";

/**
 * Whether calendar modal shell animations should be skipped.
 *
 * @returns True when reduced motion is preferred or UI motion is disabled.
 */
const shouldSkipCalendarModalAnimation = (): boolean => {
    if ( !UI_MOTION.enabled ) {
        return true;
    }

    if ( typeof window === "undefined" ) {
        return false;
    }

    return window.matchMedia( REDUCED_MOTION_MEDIA_QUERY ).matches;
};

/**
 * Resolves animation duration for the calendar modal shell.
 *
 * @param durationMs Default duration in milliseconds.
 * @returns Zero when motion should be instant.
 */
const resolveCalendarModalDurationMs = ( durationMs: number ): number => {
    return shouldSkipCalendarModalAnimation() ? 0 : durationMs;
};

/**
 * Builds the Ionic enter animation for the calendar modal card.
 *
 * @param baseEl Host ion-modal element.
 * @returns Composed enter animation.
 */
export const createCalendarModalEnterAnimation = ( baseEl: HTMLElement ): Animation => {
    const root = baseEl.shadowRoot;
    const durationMs = resolveCalendarModalDurationMs( UI_MOTION.duration.sheetMs );

    const backdropAnimation = createAnimation().
        addElement( root?.querySelector( "ion-backdrop" ) ?? baseEl ).
        fromTo( "opacity", "0.01", "var(--backdrop-opacity, 0.35)" );

    const wrapperAnimation = createAnimation().
        addElement( root?.querySelector( ".modal-wrapper" ) ?? baseEl ).
        keyframes( [
            {
                "offset":    0,
                "opacity":   "0",
                "transform": "scale(0.94) translateY(16px)",
            },
            {
                "offset":    1,
                "opacity":   "1",
                "transform": "scale(1) translateY(0)",
            },
        ] );

    return createAnimation().
        addElement( baseEl ).
        duration( durationMs ).
        easing( `cubic-bezier(${ UI_MOTION.easingIos.join( ", " ) })` ).
        addAnimation( [ backdropAnimation, wrapperAnimation ] );
};

/**
 * Builds the Ionic leave animation for the calendar modal card.
 *
 * @param baseEl Host ion-modal element.
 * @returns Composed leave animation.
 */
export const createCalendarModalLeaveAnimation = ( baseEl: HTMLElement ): Animation => {
    const root = baseEl.shadowRoot;
    const durationMs = resolveCalendarModalDurationMs( UI_MOTION.duration.fastMs );

    const backdropAnimation = createAnimation().
        addElement( root?.querySelector( "ion-backdrop" ) ?? baseEl ).
        fromTo( "opacity", "var(--backdrop-opacity, 0.35)", "0.01" );

    const wrapperAnimation = createAnimation().
        addElement( root?.querySelector( ".modal-wrapper" ) ?? baseEl ).
        keyframes( [
            {
                "offset":    0,
                "opacity":   "1",
                "transform": "scale(1) translateY(0)",
            },
            {
                "offset":    1,
                "opacity":   "0",
                "transform": "scale(0.96) translateY(10px)",
            },
        ] );

    return createAnimation().
        addElement( baseEl ).
        duration( durationMs ).
        easing( `cubic-bezier(${ UI_MOTION.easingIos.join( ", " ) })` ).
        addAnimation( [ backdropAnimation, wrapperAnimation ] );
};
